import type { ReactNode } from "react";
import { Folio, Line, d } from "./Chapters";

export { CONTACT_EMAIL } from "./Chapters";

const P = ({ children, lead }: { children: ReactNode; lead?: boolean; i?: number }) => (
  <p className={lead ? "ed-lead" : "ed-body ed-muted"}>{children}</p>
);

/* 03 — Expertise: one editorial index. Every service reads the same way —
   number + heading left, lead and supporting copy right — so emphasis comes
   from scale and accents, never from changing the reading direction. */
type Svc = { n: string; lines: string[]; lead: string; body: string[]; proof?: boolean };
const SERVICES: Svc[] = [
  {
    n: "01", lines: ["Creative Education &", "Institutional Development"],
    lead: "I offer strategic consulting to investors, founders and education leaders looking to establish, expand and commercially develop creative education institutions.",
    body: [
      "From new institutional concepts and strategic planning to expansion, commercial models and long-term growth, I bring the perspective of someone who has built and scaled education businesses from the ground up.",
      "My focus is on helping ambitious organisations create educational institutions that are commercially sustainable, relevant to their industries and positioned for the future.",
    ],
  },
  {
    n: "02", lines: ["Student Recruitment,", "Marketing & Growth"],
    lead: "Student recruitment has been central to my career, from leading recruitment and sales at BIMM to building a global online education business.",
    body: [
      "I bring that experience to educational institutions looking to attract more students, strengthen their marketing and grow enrolments.",
      "Alongside strategic consulting, I have an established creative and marketing team with expertise in content production, social media, paid advertising, audience development and digital customer acquisition.",
      "Together, we combine traditional recruitment experience with modern digital marketing to build strategies for sustainable growth.",
    ],
  },
  {
    n: "03", proof: true, lines: ["Artist Growth, Fanbase", "Development & Music", "Industry Strategy"],
    lead: "I've spent more than 25 years working across the music industry, building businesses that help artists reach new audiences, grow their fanbases and develop sustainable careers.",
    body: [
      "Through DK Artists, DK Music Business Academy and my own media platforms, I've developed extensive experience in artist marketing, audience development, music education and the evolving digital music landscape.",
      "My expertise lies in understanding how artists get discovered, turn listeners into fans and build momentum in an increasingly competitive industry.",
      "I bring that experience to record labels, distributors, music companies, educational institutions and cultural organisations looking to strengthen their artist-development strategies, expand audiences and create programmes that support the next generation of creative talent.",
    ],
  },
  {
    n: "04", lines: ["Strategic Advisory &", "Board Opportunities"],
    lead: "As a serial entrepreneur with experience founding, scaling and selling businesses, I bring an entrepreneurial and commercial perspective to organisations navigating growth, transformation and new opportunities.",
    body: [
      "I'm particularly interested in strategic advisory and board-level opportunities within education, music, entertainment and the wider creative industries.",
      "My experience spans institutional development, commercial growth, student recruitment, digital marketing and the evolving relationship between education and industry.",
    ],
  },
];

const Service = ({ s }: { s: Svc }) => (
  <article data-rule className="ed-grid ex-item">
    <header data-reveal className="ex-head">
      <span className="ex-n" aria-hidden>{s.n}</span>
      <h3 className="ex-h">{s.lines.join(" ")}</h3>
    </header>
    <div data-reveal className="ex-body">
      <P lead>{s.lead}</P>
      {s.body.map((b, i) => <P key={i} i={i + 1}>{b}</P>)}
      {s.proof && <P i={s.body.length + 1}>I grew DK Music Business Academy from zero to over 30,000 paying students in just 12 months.</P>}
    </div>
  </article>
);

export const Expertise = () => (
  <section id="expertise" className="ed-section ex">
    <div className="ed-wrap">
      <Folio n="03" label="Expertise" />
      <h2 data-lines className="ed-title ex-title" aria-label="Where I can make an impact.">
        <Line i={0}>Where I can</Line>
        <Line i={1}>make an impact.</Line>
      </h2>
      <div className="ex-index">
        {SERVICES.map((s) => <Service key={s.n} s={s} />)}
      </div>
    </div>
  </section>
);
